import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import PageHeader from "@/components/PageHeader";
import SectionHeading from "@/components/SectionHeading";
import ClosingCTA from "@/components/ClosingCTA";
import { Compass, ShieldCheck, CalendarRange, HeartHandshake, GraduationCap, Users, Award } from "lucide-react";
import { useT, useLang } from "@/i18n/LanguageContext";

const SPECIALISTS = {
    en: [
        { Icon: Compass,        role: "Strategic Counselor",  sub: "Direction and long-term planning", bullets: [
            "Builds the Comprehensive Student Evaluation",
            "Sets Ivy / Top 15 targets and defines the ideal school profile",
            "Designs the College Roadmap across Grades 9–12",
            "Recalibrates the strategy every year as your child grows",
        ]},
        { Icon: ShieldCheck,    role: "Admissions Officer",   sub: "Quality and institutional perspective", bullets: [
            "Former admissions officer from a top-tier university",
            "Reviews every deliverable with a critical institutional lens",
            "Pressure-tests the narrative, school list, and application materials",
            "Catches weaknesses long before submission",
        ]},
        { Icon: CalendarRange,  role: "Resource Manager",     sub: "Execution and opportunities", bullets: [
            "Sources research placements, summer programs, and competitions",
            "Manages AP and SAT timelines, scholarships, and awards calendars",
            "Tracks every milestone across three years",
            "Turns strategy into concrete, scheduled action",
        ]},
        { Icon: HeartHandshake, role: "Family Coach",         sub: "Mental health and family dynamics", bullets: [
            "Monitors teen mental health throughout high-pressure periods",
            "Supports parent-child communication and expectation alignment",
            "Helps the family navigate stress without damaging relationships",
            "ICF-certified — a role no other consulting practice offers",
        ]},
    ],
    zh: [
        { Icon: Compass,        role: "战略顾问",  sub: "方向与长期规划", bullets: [
            "完成《综合学生评估》",
            "设定常春藤 / Top 15 目标，并定义理想学校画像",
            "规划 9–12 年级的《升学路线图》",
            "随孩子成长每年动态调整策略",
        ]},
        { Icon: ShieldCheck,    role: "前招生官",  sub: "质量与院校视角", bullets: [
            "来自顶尖大学的前任招生官",
            "以院校审阅的视角审查每份产出",
            "对叙事、选校名单与申请材料进行压力测试",
            "在递交之前就发现弱点",
        ]},
        { Icon: CalendarRange,  role: "资源经理",  sub: "执行与机会", bullets: [
            "对接科研实习、夏校与竞赛资源",
            "管理 AP 与 SAT 时间线、奖学金与奖项日历",
            "在三年中追踪每一个里程碑",
            "把战略变成可执行、有日程的行动",
        ]},
        { Icon: HeartHandshake, role: "家庭教练",  sub: "心理健康与家庭关系", bullets: [
            "在高压期监测青少年心理健康",
            "协助亲子沟通与期望对齐",
            "帮助家庭穿越压力期而不损伤关系",
            "ICF 认证 —— 行业内独一份的角色",
        ]},
    ],
};

const DIMENSIONS = {
    en: [
        { title: "Comprehensive Student Evaluation", tagline: "The foundation. Before any planning begins, we map who your child actually is.", items: [
            "Personality, learning style and executive function assessment",
            "Gallup StrengthsFinder — full 34-dimension talent profile",
            "Academic interest mapping and potential major recommendations",
            "Career trajectory — a 20-year directional read",
            "Student profile: strengths, gaps, and risk points",
            "Family education style assessment — expectations, involvement, alignment",
        ], deliverable: "Comprehensive Student Report" },
        { title: "College Strategy Planning", tagline: "Where to aim — and why. A living strategy, not a static list.", items: [
            "Dream schools (Ivy League / Top 15) target setting — data-grounded, no false promises",
            "Ideal school profile: academic fit, culture, activities, location",
            "Reach / Match / Safety portfolio — balanced, risk-managed",
            "Major–school alignment logic",
            "Annual recalibration — the list evolves as your child grows",
        ], deliverable: "College Selection Strategy" },
        { title: "College Roadmap (Grades 9–12)", tagline: "The operational plan. Five parallel tracks, running simultaneously across three years.", items: [
            "GPA targets, AP sequencing, SAT milestones and testing timeline",
            "Retake planning and score optimization",
            "Clubs: participate → contribute → lead",
            "Sports: recreational vs. recruit track",
            "Arts: portfolio development and competitions",
            "Volunteering: long-term, impact-driven projects",
            "Pre-college courses, online learning, and summer programs",
            "Research: independent or mentor-led, with publishable output",
            "Projects: engineering, business, or social impact",
            "Competitions: AMC, ISEF, DECA, and equivalents",
            "Awards and honors: National / State / School pipeline with hit-rate strategy",
        ], deliverable: "College Roadmap with milestone tracking" },
        { title: "Progress Report + Regular Follow-Up Meetings", tagline: "The accountability structure. Strategy means nothing without execution.", groups: [
            { h: "1) Core Academics", items: ["Course selection", "Academic criteria — GPA and rigor balance", "Standardized tests — SAT / AP planning"] },
            { h: "2) Extracurricular Activity", items: ["Required activities — matched to target direction", "Optional activities — interest exploration", "Annual activity goal: Explore → Develop → Lead", "Activity depth design: participate → deepen → impact"] },
            { h: "3) Academics Theme and Achievement", items: ["Research / Projects — output-driven", "Summer programs", "Specialties — Art / Athlete / Talent"] },
            { h: "4) Personal Growth and Family Relationships", items: ["Basic skills: writing, public speaking, STEM fluency", "Interest exploration: videos, seminars, external exposure", "Career path — aligned with the evaluation report", "Family relationships", "Teenagers' mental health"] },
        ], deliverable: "Annual Plan + Monthly Progress Report" },
    ],
    zh: [
        { title: "综合学生评估", tagline: "一切从这里开始。在做任何规划之前，我们先真正了解孩子是谁。", items: [
            "性格、学习风格与执行功能评估",
            "学术兴趣地图与潜在专业方向建议",
            "职业轨迹分析 —— 未来 20 年的方向性解读",
            "学生综合画像：优势、短板与潜在风险点",
            "家庭教育风格评估 —— 期望值、参与度与一致性",
        ], deliverable: "综合学生评估报告" },
        { title: "升学战略规划", tagline: "明确目标，以及为什么要设定这些目标。这是一份动态战略，而不是一成不变的学校名单。", items: [
            "冲刺校（常春藤 / 美国前 15）目标设定 —— 基于真实数据，绝不空口承诺",
            "理想学校画像：学术匹配度、校园文化、活动资源、地理位置",
            "冲刺校 / 匹配校 / 保底校的组合 —— 均衡配置，有效控制风险",
            "专业方向与目标学校的匹配逻辑",
            "每年动态调整 —— 学校名单随着孩子的成长不断优化",
        ], deliverable: "升学选校战略报告" },
        { title: "升学路线图（9–12 年级）", tagline: "把战略变成可执行的蓝图。五大维度并行，三年持续推进。", groups: [
            { h: "学业与标化",   items: ["GPA 阶段目标、AP 课程排序、SAT 考试节点与时间线", "重考规划与分数优化策略"] },
            { h: "课外活动",     items: ["社团：从参与到贡献，再到领导角色", "体育：兴趣培养型 vs 运动员招募路径", "艺术：作品集规划与竞赛安排", "志愿服务：长期投入、有真实影响力的项目"] },
            { h: "学术拓展",     items: ["Pre-college 课程、在线学习平台与夏校申请", "科研项目：独立研究或导师带领，产出可发表的成果", "实践项目：工程、商业或社会影响力方向"] },
            { h: "竞赛与荣誉",   items: ["竞赛规划：AMC、ISEF、DECA 等", "奖项路径：国家级 / 州级 / 校级，以及命中率策略"] },
        ], deliverable: "升学路线图" },
        { title: "进度追踪与定期复盘", tagline: "没有执行，战略就是空谈。我们建立清晰的问责机制。", groups: [
            { h: "1) 核心学业",            items: ["课程选择策略", "GPA 与课程难度的平衡", "SAT / AP 考试规划"] },
            { h: "2) 课外活动",            items: ["核心活动：与目标方向高度匹配", "拓展活动：兴趣探索与尝试", "年度活动进阶目标：探索 → 发展 → 领导", "活动深度设计：参与 → 深耕 → 产生影响力"] },
            { h: "3) 学术成果",            items: ["科研项目 / 实践项目 —— 以成果为导向", "夏校经历", "特长发展 —— 艺术 / 体育 / 才艺"] },
            { h: "4) 个人成长与家庭关系",  items: ["基础能力：写作、公众表达、科学素养", "兴趣探索：在线课程、研讨会、行业接触", "职业方向 —— 与学生评估报告对齐", "家庭沟通状态", "青少年心理健康关注"] },
        ], deliverable: "年度规划 + 月度进度报告" },
    ],
};

const PARTNERSHIP = {
    en: [
        { Icon: GraduationCap, label: "Student Effort",      body: "Curiosity, discipline, initiative, and the willingness to keep showing up. This is the center of the process, and it is the part no adult can replace." },
        { Icon: Users,         label: "Parent Engagement",   body: "Calibrated expectations, steady support, and honest conversation at home. Parents help create the emotional environment in which ambition can stay healthy." },
        { Icon: Award,         label: "Counseling Expertise", body: "Senior admissions judgment, coordinated planning, institutional insight, and an operating system that turns long-term intention into measurable outcomes." },
    ],
    zh: [
        { Icon: GraduationCap, label: "学生的努力",       body: "好奇心、自律、每天到位的意愿。这是最重要的一环，也是我们无法替代的。" },
        { Icon: Users,         label: "家长的参与",       body: "合理的期望、稳定的支持、家中坦诚的对话。没有这些，再好的战略也会很快失效。" },
        { Icon: Award,         label: "顾问团队的专业",   body: "协同的专家团队、大学招生办的内部视角，以及把想法变成结果的运营系统。" },
    ],
};

const LABELS = {
    en: {
        beliefEyebrow: "Our Belief",
        beliefAuth: "Authentic Growth",
        beliefQuoteLine1: "\u201CMotivation cannot be imposed. It must be awakened.",
        beliefQuoteLine2: "We build students for themselves first — and for applications second.\u201D",
        beliefBody1: "The years before application season are the most important years to shape a student's direction. This is when curiosity can become commitment, scattered activities can become a meaningful pattern, and external pressure can be replaced by internal ownership.",
        beliefBody2: "Through challenge, guidance, and genuine belief in each student, we cultivate what no application consultant can manufacture at the last minute: discipline that comes from within, a sense of direction the student can explain in their own words, and the quiet confidence to pursue something deeply.",
        beliefBody3: "By the time application season arrives, the story does not need to be forced. Colleges see the visible result of years of authentic growth — and the student carries forward habits, judgment, and self-knowledge that matter long after any acceptance letter.",
        specialistsEyebrow: "Who Serves Your Family",
        specialistsTitlePre: "for one student",
        specialistsTitlePost: "/ one family.",
        specialistsTitleItalic: "Four experts",
        specialistsBody: "Every decision passes through all four. Your child is never handed off.",
        specialistsFootnote: "Four specialists. One coordinated team. A combined perspective no single consultant can match — and a level of care your child will feel for three years.",
        workflowEyebrow: "How We Help You",
        workflowTitle: "Our workflow,",
        workflowItalic: "built for the long run.",
        workflowSub: "A coordinated practice — assessment, strategy, roadmap, and accountability — repeated and refined year after year, from Grade 9 through application season.",
        deliverable: "→ Deliverable",
        partnershipEyebrow: "Our Partnership",
        partnershipTitle: "Three forces,",
        partnershipItalic: "one shared mission.",
        partnershipSub: "Outcomes do not come from one meeting, one essay, or one counselor. They come from the way three groups work together, month after month and year after year.",
    },
    zh: {
        beliefEyebrow: "我们的信念",
        beliefAuth: "真实的成长",
        beliefQuoteLine1: "\u201C动力没法被强加，只能被唤醒。",
        beliefQuoteLine2: "我们培养的是一个完整的人，而不仅仅是一份申请材料。\u201D",
        beliefBody1: "通过挑战、引导和对每一个孩子发自内心的信任，我们的学生拥有：主动做事的自律、自己找到的方向感，以及不需要表演、也能被看见的自信。",
        beliefBody2: "等到申请季来临，一切都水到渠成。大学看到的，是这几年真实成长留下的痕迹——而孩子真正带走的，比任何一封录取通知书都更有价值。",
        beliefBody3: "",
        specialistsEyebrow: "我们的团队",
        specialistsTitlePre: "服务一个学生",
        specialistsTitlePost: "。",
        specialistsTitleItalic: "四位专家，",
        specialistsBody: "每一个决定，都经过四位专家共同把关。你的孩子不会被转手给不同的人。",
        specialistsFootnote: "四位专家 · 一个协同的团队。单一顾问无法做到的多维视角 —— 以及孩子能在三年里持续感受到的关怀。",
        workflowEyebrow: "我们如何帮助你",
        workflowTitle: "一套协同运作的",
        workflowItalic: "服务体系。",
        workflowSub: "评估、战略、路线图、追踪反馈 —— 从九年级到申请季，每年不断细化、迭代。",
        deliverable: "→ 里程碑",
        partnershipEyebrow: "我们的合作",
        partnershipTitle: "三股力量，",
        partnershipItalic: "同一个使命。",
        partnershipSub: "动态合作 —— 月复一月，年复一年。",
    },
};

export default function CollegeStrategy() {
    const t = useT();
    const { lang } = useLang();
    const p = t.pages.strategy;
    const specialists = SPECIALISTS[lang];
    const dimensions = DIMENSIONS[lang];
    const partnership = PARTNERSHIP[lang];
    const L = LABELS[lang];

    return (
        <main data-testid="page-college-strategy" className="min-h-screen">
            <Nav />
            <PageHeader
                eyebrow={p.eyebrow}
                title={p.title}
                italicWord={p.italic}
                subtitle={p.subtitle}
                breadcrumb={[{ label: p.crumbCollege, to: "/college/strategic-planning" }, { label: p.crumbSelf }]}
                background="var(--paper)"
                stack
                subtitleNowrap
            />

            <section className="section-x" style={{ background: "var(--mist)", paddingTop: "clamp(40px,4vw,64px)", paddingBottom: "clamp(40px,4vw,64px)" }}>
                <div className="grid grid-cols-12 gap-x-12 gap-y-10 items-start">
                    <aside className="col-span-12 lg:col-span-4">
                        <div className="overflow-hidden" style={{ aspectRatio: "1/1", border: "1px solid var(--line)", maxWidth: 360 }}>
                            <img
                                src="https://images.unsplash.com/photo-1481627834876-b7833e8f5570?w=900&q=85&auto=format&fit=crop"
                                alt="Open book on a quiet desk"
                                className="w-full h-full object-cover"
                                style={{ filter: "grayscale(40%) contrast(1.05)" }}
                            />
                        </div>
                        <p className="eyebrow mt-3">{L.beliefAuth}</p>
                    </aside>

                    <div className="col-span-12 lg:col-span-8 lg:pl-2">
                        <div className="flex items-center gap-3">
                            <div style={{ width: 28, height: 1, background: "var(--orange)" }} />
                            <p className="font-mono" style={{ fontSize: 11, letterSpacing: "0.24em", textTransform: "uppercase", color: "var(--orange)", fontWeight: 500 }}>
                                {L.beliefEyebrow}
                            </p>
                        </div>

                        <p className="font-display mt-5" style={{ fontSize: "clamp(22px, 1.9vw, 30px)", lineHeight: 1.4, color: "var(--orange)", fontWeight: 500 }}>
                            {L.beliefQuoteLine1}
                            <br />
                            {L.beliefQuoteLine2}
                        </p>

                        <div className="mt-6" style={{ width: 36, height: 2, background: "var(--navy)" }} />

                        <p className="font-serif mt-6" style={{ fontSize: 18, lineHeight: 1.75, color: "var(--ink)" }}>{L.beliefBody1}</p>
                        <p className="font-serif mt-4" style={{ fontSize: 18, lineHeight: 1.75, color: "var(--ink)" }}>{L.beliefBody2}</p>
                        <p className="font-serif mt-4" style={{ fontSize: 18, lineHeight: 1.75, color: "var(--ink)" }}>{L.beliefBody3}</p>
                    </div>
                </div>
            </section>

            {/* Specialists */}
            <section className="section-x" style={{ background: "var(--paper)", paddingTop: "clamp(40px,4vw,64px)", paddingBottom: "clamp(40px,4vw,64px)" }}>
                <div className="flex flex-col items-start">
                    <div className="flex items-center gap-3">
                        <div style={{ width: 28, height: 1, background: "var(--orange)" }} />
                        <p className="font-mono" style={{ fontSize: 11, letterSpacing: "0.24em", textTransform: "uppercase", color: "var(--orange)", fontWeight: 500 }}>
                            {L.specialistsEyebrow}
                        </p>
                    </div>
                    <h2 className="font-display mt-5" style={{ fontSize: "clamp(32px, 3vw, 44px)", lineHeight: 1.15, letterSpacing: "-0.01em", fontWeight: 500, color: "var(--navy)", maxWidth: 880 }}>
                        <em style={{ fontStyle: "normal", color: "var(--orange)", fontWeight: 500 }}>{L.specialistsTitleItalic}</em>{" "}
                        {L.specialistsTitlePre} <span style={{ color: "var(--navy)" }}>{L.specialistsTitlePost}</span>
                    </h2>
                    <p className="font-serif mt-5" style={{ fontSize: 18, lineHeight: 1.65, color: "var(--ink)", maxWidth: 760 }}>{L.specialistsBody}</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-10">
                    {specialists.map((s, i) => {
                        const Icon = s.Icon;
                        const palettes = [
                            { bg: "var(--mist)",      accent: "var(--orange)" },
                            { bg: "var(--mist-deep)", accent: "var(--orange)" },
                            { bg: "var(--mist-deep)", accent: "var(--orange)" },
                            { bg: "var(--mist)",      accent: "var(--orange)" },
                        ];
                        const palette = palettes[i % palettes.length];
                        return (
                            <article key={i} className="p-7 lg:p-8 relative overflow-hidden lift" style={{ background: palette.bg, border: "1px solid var(--line)" }} data-testid={`specialist-${i}`}>
                                <div aria-hidden="true" style={{ position: "absolute", right: -36, top: -36, width: 120, height: 120, borderRadius: "50%", background: "var(--paper)", opacity: 0.6 }} />
                                <div className="relative">
                                    <div className="flex items-center gap-4">
                                        <div className="flex items-center justify-center pulse-circle" style={{ width: 52, height: 52, borderRadius: "50%", background: palette.accent, color: "#fff", boxShadow: "0 4px 14px rgba(255,152,0,0.25)", flexShrink: 0 }}>
                                            <Icon size={24} strokeWidth={1.75} />
                                        </div>
                                        <h3 className="font-display" style={{ fontSize: 24, lineHeight: 1.2, color: "var(--navy)", fontWeight: 600, letterSpacing: "-0.005em" }}>{s.role}</h3>
                                    </div>
                                    <p className="font-mono mt-3" style={{ fontSize: 11, letterSpacing: "0.2em", textTransform: "uppercase", color: "var(--orange)", fontWeight: 500 }}>{s.sub}</p>
                                    <div className="my-4" style={{ width: 28, height: 2, background: "var(--orange)" }} />
                                    <ul className="space-y-2.5">
                                        {s.bullets.map((b, j) => (
                                            <li key={j} className="flex items-start gap-3">
                                                <span aria-hidden="true" style={{ marginTop: 8, width: 5, height: 5, background: "var(--navy)", flexShrink: 0, borderRadius: "50%" }} />
                                                <p className="font-serif" style={{ fontSize: 15, lineHeight: 1.55, color: "var(--ink)" }}>{b}</p>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </article>
                        );
                    })}
                </div>

                <p className="font-serif mt-8" style={{ fontSize: 20, lineHeight: 1.55, color: "var(--navy)", maxWidth: 960 }}>
                    {L.specialistsFootnote}
                </p>
            </section>

            {/* How We Help You */}
            <section className="section-x" style={{ background: "var(--mist)", paddingTop: "clamp(40px,4vw,64px)", paddingBottom: "clamp(40px,4vw,64px)" }}>
                <SectionHeading
                    eyebrow={L.workflowEyebrow}
                    title={L.workflowTitle}
                    italic={L.workflowItalic}
                    subtitle={L.workflowSub}
                    nowrap
                />

                <ol className="mt-10" style={{ borderTop: "2px solid var(--navy)" }}>
                    {dimensions.map((d, i) => (
                        <li
                            key={i}
                            className="workflow-row"
                            style={{ borderBottom: "1px solid var(--line)" }}
                            data-testid={`dimension-${i}`}
                        >
                            <div className="workflow-row-inner grid grid-cols-12 gap-x-8 gap-y-4">
                                <div className="col-span-12 lg:col-span-4 workflow-left">
                                    <span className="workflow-num font-display" style={{ fontSize: 44, lineHeight: 0.9, color: "var(--orange)", fontWeight: 600 }}>{String(i + 1).padStart(2, "0")}</span>
                                    <h3 className="font-display mt-3" style={{ fontSize: "clamp(22px, 1.75vw, 28px)", lineHeight: 1.2, color: "var(--navy)", fontWeight: 600, letterSpacing: "-0.005em" }}>{d.title}</h3>
                                    <p className="font-serif mt-2" style={{ fontSize: 16.5, lineHeight: 1.55, color: "var(--steel)" }}>{d.tagline}</p>
                                </div>
                                <div className="col-span-12 lg:col-span-8">
                                    {d.items && (
                                        <ul className="space-y-2">
                                            {d.items.map((it, j) => (
                                                <li key={j} className="flex items-start gap-3">
                                                    <span className="font-mono" style={{ fontSize: 11, color: "var(--orange)", marginTop: 7 }}>▸</span>
                                                    <p className="font-serif" style={{ fontSize: 17, lineHeight: 1.55, color: "var(--ink)" }}>{it}</p>
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                    {d.groups && (
                                        <div className="space-y-4">
                                            {d.groups.map((g, j) => (
                                                <div key={j}>
                                                    <p className="font-mono" style={{ fontSize: 11.5, letterSpacing: "0.18em", textTransform: "uppercase", color: "var(--orange)", fontWeight: 500 }}>{g.h}</p>
                                                    <ul className="mt-2 space-y-2">
                                                        {g.items.map((it, k) => (
                                                            <li key={k} className="flex items-start gap-3">
                                                                <span className="font-mono" style={{ fontSize: 11, color: "var(--orange)", marginTop: 7 }}>▸</span>
                                                                <p className="font-serif" style={{ fontSize: 17, lineHeight: 1.55, color: "var(--ink)" }}>{it}</p>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                    <div className="mt-4 pt-3" style={{ borderTop: "1px solid var(--line)" }}>
                                        <p className="font-mono" style={{ fontSize: 10.5, letterSpacing: "0.22em", textTransform: "uppercase", color: "var(--steel)" }}>{L.deliverable}</p>
                                        <p className="font-display mt-1" style={{ fontSize: 18, color: "var(--navy)", fontWeight: 600 }}>{d.deliverable}</p>
                                    </div>
                                </div>
                            </div>
                        </li>
                    ))}
                </ol>
            </section>

            {/* Our Partnership */}
            <section className="section-x" style={{ background: "var(--paper)", paddingTop: "clamp(40px,4vw,64px)", paddingBottom: "clamp(40px,4vw,64px)" }}>
                <SectionHeading
                    eyebrow={L.partnershipEyebrow}
                    title={L.partnershipTitle}
                    italic={L.partnershipItalic}
                    subtitle={L.partnershipSub}
                />

                <div className="partnership-group mt-10 grid grid-cols-1 lg:grid-cols-3 gap-x-12 gap-y-10 relative" style={{ borderTop: "2px solid var(--navy)", paddingTop: 40 }}>
                    {/* connector arcs between the three icons, drawn from card 1 → 2 → 3 */}
                    <div aria-hidden="true" className="partnership-connector partnership-connector-1" />
                    <div aria-hidden="true" className="partnership-connector partnership-connector-2" />
                    {partnership.map((pp, i) => {
                        const Icon = pp.Icon;
                        return (
                            <div
                                key={i}
                                data-testid={`partnership-${i}`}
                                className="partnership-card"
                                style={{ "--partnership-i": i }}
                            >
                                <div className="flex flex-col items-center text-center">
                                    <div className="partnership-icon">
                                        <Icon size={30} strokeWidth={1.75} />
                                    </div>
                                    <p className="font-mono mt-5" style={{ fontSize: 14, letterSpacing: "0.22em", textTransform: "uppercase", color: "var(--navy)", fontWeight: 600 }}>
                                        {pp.label}
                                    </p>
                                    <div className="partnership-rule mt-4" />
                                </div>
                                <p className="font-serif mt-5" style={{ fontSize: 17, lineHeight: 1.7, color: "var(--ink)", textAlign: "center", maxWidth: 380, margin: "20px auto 0" }}>
                                    {pp.body}
                                </p>
                            </div>
                        );
                    })}
                </div>
            </section>

            <ClosingCTA />
            <Footer />
        </main>
    );
}
